import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { standardStrategy } from "piral-base";
import { createBlazorApi } from "piral-blazor";
import { createInstance, Piral, SetComponent } from "piral-core";
import * as React from "react";
import { createRoot } from "react-dom/client";
import { Route, Switch } from "react-router";
import "./assets/styles/main.scss";
import { ErrorComponent } from "./components/ErrorComponent";
import { errors, TooManyAttemptsError } from "./components/Errors";
import { LoadingSpinner } from "./components/LoadingSpinner";
import "./infrastructure/browser";
import { env } from "./infrastructure/environment";
import "./infrastructure/legacy";
import { getInitialLanguage } from "./infrastructure/localization";
import {
  getCurrentAccount,
  getCurrentUser,
  getMsalInstance,
} from "./infrastructure/msal";
import {
  redirectIfAppUnavailable,
  redirectToFirstApp,
  requestPilets,
} from "./infrastructure/requestPilets";
import { RouteChangeEmitter } from "./infrastructure/routeChangeEmitter";
import "./infrastructure/tenant";
import { layout } from "./layout";
import {
  SwitchToLayoutDefault,
  SwitchToLayoutEmpty,
  SwitchToLayoutNoSidebar,
} from "./layoutSwitchers";
import { LogoutPage } from "./pages/LogoutPage";
import getAzureApplicationInsights from "./plugins/applicationInsights";
import datadogRum from "./plugins/datadog";
import "./plugins/monaco";
import * as userflow from "./plugins/userflow";

/** Detect AADB2C90157 retry-limit error */
const isTooManyAttempts = (err: unknown): boolean => {
  try {
    return JSON.stringify(err).includes("AADB2C90157");
  } catch {
    return false;
  }
};

//enable debugging
if (env.DEBUG_PILET === "true") {
  sessionStorage.setItem("dbg:load-pilets", "on");
}

async function main() {
  try {
    const appInsights = await getAzureApplicationInsights();
    //initialize the piral instance
    const piralInstance = createInstance({
      state: {
        components: layout,
        errorComponents: errors,
      },
      plugins: [
        createBlazorApi({
          lazy: true,
          logLevel: 3,
          initialLanguage: getInitialLanguage(),
          //onLanguageChange: callback => {}
        }),
      ],
      requestPilets() {
        return requestPilets();
      },
      async: async (options, pilets) => {
        appInsights.trackTrace({
          message: "Start fetching pilets",
          severityLevel: SeverityLevel.Information,
        });
        const piletList = await options.fetchPilets();
        const pathParts = window.location.pathname.split("/").filter(Boolean);
        if ((window.location.pathname === "/" || pathParts.length === 1) && piletList.length > 0) {
          await redirectToFirstApp(piletList);
        } else if (pathParts.length >= 2) {
          await redirectIfAppUnavailable(piletList);
        }
        appInsights.trackTrace(
          {
            message: "Pilets fetched",
            severityLevel: SeverityLevel.Information,
          },
          { piletCount: piletList.length, pilets: piletList }
        );
        piralInstance.root.setData("pilets", piletList);
        standardStrategy(options, pilets);
        return new Promise((resolve) => {
          window.addEventListener(
            "loaded-blazor-pilet",
            async (ev: CustomEvent) => {
              if (ev.detail.name === "@casablanca/cloud-ui") {
                // The layout has been loaded completely. We can now hide the spinner and show the app.
                resolve();
              }
            }
          );
        });
      },
    });

    //initialize msal authentication
    const msalInstance = await getMsalInstance();
    await msalInstance.initialize();
    await msalInstance.handleRedirectPromise().then((result) => {
      if (result) {
        msalInstance.setActiveAccount(result.account);
      }
    });
    await getCurrentAccount();
    const user = await getCurrentUser();

    // Identify user in Datadog RUM
    datadogRum.setUser({
      id: user.id,
      email: user.email,
      name: `${user.firstName} ${user.lastName}`.trim() || user.email,
    });

    userflow.identifyUser(user);

    // Track initial view in Datadog RUM
    datadogRum.startView(window.location.pathname);

    //render piral right after authenticating
    const root = createRoot(document.querySelector("#app"));
    root.render(
      <Piral instance={piralInstance}>
        <SetComponent name="LoadingIndicator" component={LoadingSpinner} />
        <RouteChangeEmitter>
          <Switch>
            <Route path="/logout" exact component={LogoutPage} />
            <Route
              path="/:tenantId"
              exact
              component={SwitchToLayoutNoSidebar}
            />
            <Route
              path="/users/current"
              exact
              component={SwitchToLayoutNoSidebar}
            />
            <Route
              path="/iam/resourceselection"
              exact
              component={SwitchToLayoutNoSidebar}
            />
            <Route
              path="/iam/organizations"
              exact
              component={SwitchToLayoutNoSidebar}
            />
            <Route
              path="/iam/invitations"
              exact
              component={SwitchToLayoutNoSidebar}
            />
            <Route
              path="/:tenantId/wizard"
              component={SwitchToLayoutNoSidebar}
            />
            <Route
              path="/iam/userprofile"
              exact
              component={SwitchToLayoutNoSidebar}
            />
            <Route
              path="/:tenantId/licensing/request/:moduleIdentifier"
              exact
              component={SwitchToLayoutNoSidebar}
            />
            <Route
              path="/:tenantId/legacy/:legacyPath"
              exact
              component={SwitchToLayoutNoSidebar}
            />
            <Route path="/authentication/*" component={SwitchToLayoutEmpty} />
            <Route path="/app/*" component={SwitchToLayoutNoSidebar} />
            <Route path="/" exact component={SwitchToLayoutNoSidebar} />
            <Route path="/" component={SwitchToLayoutDefault} />
          </Switch>
        </RouteChangeEmitter>
      </Piral>
    );
    window.piralInstance = piralInstance.root;
  } catch (error) {
    const root = createRoot(document.querySelector("#app"));
    if (isTooManyAttempts(error)) {
      root.render(<TooManyAttemptsError />);
    } else {
      root.render(
        <ErrorComponent title="Error during app initialization" error={error} />
      );
    }
  }
}

main();
